import { centsToFormattedDollars } from '@fruitful/fruitils';
import { AnalyticsUserEvent } from '@fruitful/types';
import { LinearGradient } from 'expo-linear-gradient';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
    type LayoutChangeEvent,
    type NativeScrollEvent,
    type NativeSyntheticEvent,
    Platform,
    ScrollView as RNScrollView,
} from 'react-native';
import { BarChart } from 'react-native-gifted-charts';
import {
    BodyMediumRegular,
    BodySmallRegular,
    getDefaultFontFamily,
    H2Regular,
    PillButton,
    Row,
    ScrollView,
    Spinner,
    useThemeColors,
    View,
    XStack,
    YStack,
} from 'smoothie';

import {
    type Transaction,
    TransactionDcSign,
} from '../../../banking/types/Transactions';
import { getTransactionIcon } from '../../../common/utils/bankingUtils';
import { track } from '../../../logger/Logger';
import {
    type FreeToSpendDataItem,
    type FreeToSpendDataPoint,
    type FreeToSpendPeriod,
} from '../../types/FreeToSpendTypes';

// On web, cap width at desktop modal content width but allow narrower mobile-web layouts.
const WEB_SPEND_CHART_WIDTH = 430;
const WEB_SPEND_CHART_LABEL_HEIGHT = 20;
/**
 * Web-only wrapper height around BarChart (`height={202}`). Gifted-charts draws x-axis labels with
 * absolute positioning; a fixed slot avoids first-paint jump. Any value above real layout reads as
 * empty space below the chart—keep this as tight as possible without clipping labels.
 */
const WEB_SPEND_CHART_SLOT_HEIGHT = 232;

/** Default max height for the day list when embedded in Sprout chat. */
export const FREE_TO_SPEND_EMBEDDED_DAY_LIST_MAX_HEIGHT = 300;

const EMBED_CARD_PADDING = 16;
const EMBED_DAY_GAP = 24;
const SCROLL_FADE_HEIGHT = 48;
/** Ignore sub-pixel scroll remainders when deciding if more content is below. */
const SCROLL_EDGE_THRESHOLD = 2;
/**
 * The embedded card is white so the gray chart and day sections read as
 * distinct surfaces on top of it, matching the sheet. The scroll fade blends
 * toward that white, with alpha expressed on the same RGB so iOS does not
 * render a black transparent-gradient artifact.
 */
const EMBED_CARD_HEX = '#ffffff';
const SCROLL_FADE_TRANSPARENT = `${EMBED_CARD_HEX}00`;

const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
    }).format(date);
};

const parseLocalDateFromYmd = (dateStr: string): Date => {
    const parts = dateStr.split('-').map(Number);
    const year = parts[0];
    const month = parts[1];
    const day = parts[2];
    if (
        year == null ||
        month == null ||
        day == null ||
        Number.isNaN(year) ||
        Number.isNaN(month) ||
        Number.isNaN(day)
    ) {
        return new Date(NaN);
    }
    return new Date(year, month - 1, day);
};

const formatYmdLocal = (d: Date): string => {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// monday (00:00 local) of the week containing `d` (week = Mon–Sun)
const getMondayLocal = (d: Date): Date => {
    const local = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const day = local.getDay();
    const diffFromMonday = day === 0 ? -6 : 1 - day;
    local.setDate(local.getDate() + diffFromMonday);
    return local;
};

const getMonthKeyFromYmd = (dateStr: string): string | null => {
    const d = parseLocalDateFromYmd(dateStr);
    if (Number.isNaN(d.getTime())) {
        return null;
    }
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// sortable key: YYYY-MM-DD of the Monday starting that week
const getWeekKeyFromYmd = (dateStr: string): string | null => {
    const d = parseLocalDateFromYmd(dateStr);
    if (Number.isNaN(d.getTime())) {
        return null;
    }
    return formatYmdLocal(getMondayLocal(d));
};

const formatShortMonthDay = (d: Date): string => {
    return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
    }).format(d);
};

const signedTransactionAmountCents = (transaction: Transaction): number => {
    return (
        transaction.amount *
        (transaction.dcSign === TransactionDcSign.DEBIT.toLowerCase() ? 1 : -1)
    );
};

interface ChartBarDataItem {
    periodKey: string;
    value: number;
    label: string;
    barWidth: number;
    barBorderRadius: number;
    frontColor: string;
}

const isChartBarWithPeriodKey = (item: unknown): item is ChartBarDataItem => {
    if (typeof item !== 'object' || item == null || !('periodKey' in item)) {
        return false;
    }
    const { periodKey } = item as { periodKey: unknown };
    return typeof periodKey === 'string';
};

interface SpendPeriodBarChartProps {
    chartWidth: number;
    chartData: ChartBarDataItem[];
    chartYAxisMax: number;
    showBudgetReferenceLine: boolean;
    budget: number | undefined;
    focusedBarChartIndex: number;
    onSelectPeriod: (periodKey: string) => void;
}

const SpendPeriodBarChart = ({
    chartWidth,
    chartData,
    chartYAxisMax,
    showBudgetReferenceLine,
    budget,
    focusedBarChartIndex,
    onSelectPeriod,
}: SpendPeriodBarChartProps) => {
    const { getColor } = useThemeColors();
    const barChart = (
        <BarChart
            data={chartData}
            maxValue={chartYAxisMax}
            width={chartWidth}
            height={202}
            {...(Platform.OS === 'web' ? { parentWidth: chartWidth } : {})}
            {...(Platform.OS === 'web'
                ? { xAxisLabelsHeight: WEB_SPEND_CHART_LABEL_HEIGHT }
                : {})}
            {...(Platform.OS === 'web'
                ? {
                      scrollToEnd: false,
                      scrollToIndex:
                          chartData.length > 0
                              ? chartData.length - 1
                              : undefined,
                  }
                : { scrollToEnd: true })}
            scrollAnimation={Platform.OS !== 'web'}
            hideYAxisText
            hideRules
            yAxisLabelWidth={0}
            yAxisThickness={0}
            xAxisThickness={0}
            initialSpacing={16}
            spacing={10}
            showReferenceLine1={showBudgetReferenceLine}
            referenceLine1Position={budget}
            referenceLine1Config={{
                color: '#1F1F1F',
                dashWidth: 6,
                dashGap: 3,
            }}
            focusedBarConfig={{
                color: getColor('$leaf'),
            }}
            focusedBarIndex={focusedBarChartIndex}
            onPress={(item: unknown) => {
                if (isChartBarWithPeriodKey(item)) {
                    track(AnalyticsUserEvent.FTS_TAP_BAR);
                    onSelectPeriod(item.periodKey);
                }
            }}
            focusBarOnPress
            xAxisLabelTextStyle={{
                fontFamily: getDefaultFontFamily(),
                fontSize: 14,
                fontWeight: '500',
                color: '#1F1F1F',
            }}
        />
    );

    // we need to wrap the chart in a view on web to avoid layout jumps
    if (Platform.OS === 'web') {
        return (
            <View
                alignSelf="flex-start"
                width={chartWidth}
                height={WEB_SPEND_CHART_SLOT_HEIGHT}
                overflow="hidden"
            >
                {barChart}
            </View>
        );
    }

    return barChart;
};

export const TransactionItem = ({
    transaction,
    onOpenTransaction,
    showDateSubtitle = true,
}: {
    transaction: Transaction;
    /** When omitted, the row is not pressable (no detail drill-down). */
    onOpenTransaction?: (transaction: Transaction) => void;
    /** When false, omit the date subtitle (e.g. day-grouped lists). Defaults to true. */
    showDateSubtitle?: boolean;
}) => {
    const formattedDate = formatDate(new Date(transaction.transactionTime));

    const transactionIcon = getTransactionIcon(transaction);

    return (
        <Row
            title={transaction.description}
            subtitle={showDateSubtitle ? formattedDate : undefined}
            leftIcon={transactionIcon}
            containerProps={{
                padding: 0,
                cursor: onOpenTransaction != null ? 'pointer' : 'default',
                alignItems: 'flex-start',
                backgroundColor: 'unset',
                hoverStyle: { backgroundColor: 'unset' },
                pressStyle: { backgroundColor: 'unset' },
            }}
            rightContent={
                <YStack alignItems="flex-end" gap={4}>
                    <BodyMediumRegular>
                        {centsToFormattedDollars(
                            signedTransactionAmountCents(transaction),
                        )}
                    </BodyMediumRegular>
                    {transaction.status === 'pending' ? (
                        <BodySmallRegular color="$gray1000">
                            Pending
                        </BodySmallRegular>
                    ) : null}
                </YStack>
            }
            onPress={
                onOpenTransaction != null
                    ? () => {
                          onOpenTransaction(transaction);
                      }
                    : undefined
            }
            iconProps={{
                marginTop: 4,
                color: '$black',
            }}
        />
    );
};

export const DayBox = ({
    day,
    onOpenTransaction,
}: {
    day: FreeToSpendDataPoint;
    /** When omitted, transaction rows are not pressable. */
    onOpenTransaction?: (transaction: Transaction) => void;
}) => {
    const formattedDate = formatDate(day.date);

    // add debits to total, subtract credits
    const dayTotal = day.transactions.reduce(
        (acc, transaction) => acc + signedTransactionAmountCents(transaction),
        0,
    );

    return (
        <YStack borderRadius={24} overflow="hidden">
            <XStack
                padding={16}
                backgroundColor="$gray200"
                justifyContent="space-between"
                alignItems="center"
            >
                <BodyMediumRegular>{formattedDate}</BodyMediumRegular>
                <BodyMediumRegular>
                    {centsToFormattedDollars(dayTotal, dayTotal > 0)}
                </BodyMediumRegular>
            </XStack>
            <View padding={16} backgroundColor="$gray100">
                {day.transactions.length === 0 ? (
                    <BodyMediumRegular paddingVertical={8} textAlign="center">
                        No transactions
                    </BodyMediumRegular>
                ) : (
                    <YStack gap={24}>
                        {day.transactions.map((transaction) => (
                            <TransactionItem
                                key={transaction.uuid}
                                transaction={transaction}
                                onOpenTransaction={onOpenTransaction}
                            />
                        ))}
                    </YStack>
                )}
            </View>
        </YStack>
    );
};

export interface FreeToSpendTransactionsListProps {
    spendPeriod: FreeToSpendPeriod;
    allSpendData: FreeToSpendDataItem[];
    budget: number | undefined;
    isLoadingTransactions: boolean;
    isTransactionsError?: boolean;
    onRetryTransactions?: () => void;
    /**
     * Initial bar selection. Monthly: YYYY-MM. Weekly: Monday YYYY-MM-DD.
     * When omitted or not in data, selects the latest period.
     */
    initialSelectedPeriodKey?: string | null;
    /** When omitted, transaction rows are not pressable. */
    onOpenTransaction?: (transaction: Transaction) => void;
    /**
     * When set, the day list scrolls inside this max height (Sprout chat embed).
     * When omitted, days expand fully (home TallSheet).
     */
    dayListMaxHeight?: number;
    /**
     * When true (default), measure chart width once the host is visible.
     * Sheets that remount on open can pass `isActive` to reset web chart timing.
     */
    isActive?: boolean;
}

/**
 * Shared FTS transactions list body: period bar chart + day-grouped spend.
 * Used by the home TallSheet and the Sprout chat embedded list view.
 */
export const FreeToSpendTransactionsList = ({
    spendPeriod,
    allSpendData,
    budget,
    isLoadingTransactions,
    isTransactionsError = false,
    onRetryTransactions,
    initialSelectedPeriodKey,
    onOpenTransaction,
    dayListMaxHeight,
    isActive = true,
}: FreeToSpendTransactionsListProps) => {
    const { getColor } = useThemeColors();

    // Native uses measured width; web uses min(measuredWidth, 430) to support mobile-web.
    const [chartWidth, setChartWidth] = useState<number | undefined>(
        Platform.OS === 'web' ? WEB_SPEND_CHART_WIDTH : undefined,
    );
    const [shouldRenderWebChart, setShouldRenderWebChart] = useState(
        Platform.OS !== 'web',
    );
    const isWebChartWidthLockedRef = useRef(false);

    useEffect(() => {
        if (Platform.OS !== 'web') {
            return;
        }
        if (!isActive) {
            return;
        }
        setShouldRenderWebChart(false);
        isWebChartWidthLockedRef.current = false;
        const id = setTimeout(() => {
            isWebChartWidthLockedRef.current = true;
            setShouldRenderWebChart(true);
        }, 0);
        return () => {
            clearTimeout(id);
        };
    }, [isActive]);

    const periodKeysOrdered = useMemo(() => {
        const keys = new Set<string>();
        for (const item of allSpendData) {
            const key =
                spendPeriod === 'monthly'
                    ? getMonthKeyFromYmd(item.date)
                    : getWeekKeyFromYmd(item.date);
            if (key != null) {
                keys.add(key);
            }
        }
        return Array.from(keys).sort((a, b) => a.localeCompare(b));
    }, [allSpendData, spendPeriod]);

    const [selectedPeriodKey, setSelectedPeriodKey] = useState<string | null>(
        null,
    );

    // Bottom-fade affordance for the capped day list (Sprout chat embed only).
    const [dayListViewportHeight, setDayListViewportHeight] = useState(0);
    const [dayListContentHeight, setDayListContentHeight] = useState(0);
    const [dayListScrollY, setDayListScrollY] = useState(0);

    const handleDayListLayout = (event: LayoutChangeEvent) => {
        setDayListViewportHeight(event.nativeEvent.layout.height);
    };

    const handleDayListContentSizeChange = (_width: number, height: number) => {
        setDayListContentHeight(height);
    };

    const handleDayListScroll = (
        event: NativeSyntheticEvent<NativeScrollEvent>,
    ) => {
        setDayListScrollY(event.nativeEvent.contentOffset.y);
    };

    useEffect(() => {
        if (!isActive) {
            setSelectedPeriodKey(null);
            if (Platform.OS === 'web') {
                setShouldRenderWebChart(false);
                isWebChartWidthLockedRef.current = false;
            }
            return;
        }
        setSelectedPeriodKey((prev) => {
            if (periodKeysOrdered.length === 0) {
                return null;
            }
            if (prev != null && periodKeysOrdered.includes(prev)) {
                return prev;
            }
            if (
                initialSelectedPeriodKey != null &&
                periodKeysOrdered.includes(initialSelectedPeriodKey)
            ) {
                return initialSelectedPeriodKey;
            }
            return periodKeysOrdered[periodKeysOrdered.length - 1] ?? null;
        });
    }, [isActive, periodKeysOrdered, initialSelectedPeriodKey]);

    const chartData = useMemo(() => {
        if (spendPeriod === 'monthly') {
            const monthTotals = new Map<
                string,
                { totalCents: number; year: number; monthIndex: number }
            >();

            for (const item of allSpendData) {
                const d = parseLocalDateFromYmd(item.date);
                if (Number.isNaN(d.getTime())) {
                    continue;
                }
                const year = d.getFullYear();
                const monthIndex = d.getMonth();
                const key = `${year}-${String(monthIndex + 1).padStart(2, '0')}`;

                const dayTotalCents = item.transactions.reduce(
                    (acc, transaction) =>
                        acc + signedTransactionAmountCents(transaction),
                    0,
                );

                const prev = monthTotals.get(key);
                monthTotals.set(key, {
                    totalCents: (prev?.totalCents ?? 0) + dayTotalCents,
                    year,
                    monthIndex,
                });
            }

            return Array.from(monthTotals.entries())
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([periodKey, { totalCents, year, monthIndex }]) => ({
                    periodKey,
                    value: totalCents,
                    label: new Intl.DateTimeFormat('en-US', {
                        month: 'short',
                    }).format(new Date(year, monthIndex)),
                    barWidth: 48,
                    barBorderRadius: 12,
                    frontColor: getColor('$leafLight'),
                }));
        }

        const weekTotals = new Map<
            string,
            { totalCents: number; monday: Date }
        >();

        for (const item of allSpendData) {
            const d = parseLocalDateFromYmd(item.date);
            if (Number.isNaN(d.getTime())) {
                continue;
            }
            const monday = getMondayLocal(d);
            const key = formatYmdLocal(monday);

            const dayTotalCents = item.transactions.reduce(
                (acc, transaction) =>
                    acc + signedTransactionAmountCents(transaction),
                0,
            );

            const prev = weekTotals.get(key);
            weekTotals.set(key, {
                totalCents: (prev?.totalCents ?? 0) + dayTotalCents,
                monday,
            });
        }

        return Array.from(weekTotals.entries())
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([periodKey, { totalCents, monday }]) => ({
                periodKey,
                value: totalCents,
                label: `${monday.getMonth() + 1}/${monday.getDate()}`,
                barWidth: 48,
                barBorderRadius: 12,
                frontColor: getColor('$leafLight'),
            }));
    }, [allSpendData, spendPeriod, getColor]);

    const maxBarValue = useMemo(() => {
        if (chartData.length === 0) {
            return 0;
        }
        return Math.max(...chartData.map((item) => item.value ?? 0));
    }, [chartData]);

    const showBudgetReferenceLine = useMemo(
        () =>
            budget != null &&
            chartData.some((item) => (item.value ?? 0) > budget),
        [budget, chartData],
    );

    const chartYAxisMax = useMemo(() => {
        const base = Math.max(maxBarValue, 1);
        if (!showBudgetReferenceLine || budget == null) {
            return base;
        }
        return Math.max(base, budget);
    }, [budget, maxBarValue, showBudgetReferenceLine]);

    const selectedBarTotalCents = useMemo(() => {
        if (selectedPeriodKey == null) {
            return null;
        }
        const bar = chartData.find(
            (item) => item.periodKey === selectedPeriodKey,
        );
        return bar?.value ?? null;
    }, [chartData, selectedPeriodKey]);

    const focusedBarChartIndex = useMemo(() => {
        if (selectedPeriodKey == null || chartData.length === 0) {
            return -1;
        }
        const idx = chartData.findIndex(
            (item) => item.periodKey === selectedPeriodKey,
        );
        return idx === -1 ? -1 : idx;
    }, [chartData, selectedPeriodKey]);

    const selectedSpendData = useMemo((): FreeToSpendDataPoint[] => {
        if (selectedPeriodKey == null) {
            return [];
        }
        const itemsInPeriod = allSpendData.filter((item) => {
            if (spendPeriod === 'monthly') {
                return getMonthKeyFromYmd(item.date) === selectedPeriodKey;
            }
            return getWeekKeyFromYmd(item.date) === selectedPeriodKey;
        });
        const byDate = new Map<
            string,
            { spend: number; transactions: Transaction[] }
        >();
        for (const item of itemsInPeriod) {
            const cur = byDate.get(item.date) ?? { spend: 0, transactions: [] };
            byDate.set(item.date, {
                spend: cur.spend + item.spend,
                transactions: [...cur.transactions, ...item.transactions],
            });
        }
        return Array.from(byDate.entries()).map(([dateStr, merged]) => ({
            date: parseLocalDateFromYmd(dateStr),
            amount: merged.spend,
            transactions: merged.transactions,
        }));
    }, [allSpendData, selectedPeriodKey, spendPeriod]);

    const chartPeriodCaption = useMemo(() => {
        if (selectedPeriodKey == null) {
            return null;
        }
        if (spendPeriod === 'monthly') {
            const [yearStr, monthStr] = selectedPeriodKey.split('-');
            const year = Number(yearStr);
            const month = Number(monthStr);
            if (
                yearStr == null ||
                monthStr == null ||
                Number.isNaN(year) ||
                Number.isNaN(month) ||
                month < 1 ||
                month > 12
            ) {
                return null;
            }
            const monthName = new Intl.DateTimeFormat('en-US', {
                month: 'long',
            }).format(new Date(year, month - 1));
            return `${monthName} spend`;
        }
        const monday = parseLocalDateFromYmd(selectedPeriodKey);
        if (Number.isNaN(monday.getTime())) {
            return null;
        }
        const sunday = new Date(
            monday.getFullYear(),
            monday.getMonth(),
            monday.getDate() + 6,
        );
        return `${formatShortMonthDay(monday)} - ${formatShortMonthDay(sunday)}`;
    }, [spendPeriod, selectedPeriodKey]);

    if (isLoadingTransactions) {
        return (
            <View
                width="100%"
                height="100%"
                minHeight={200}
                justifyContent="center"
                alignItems="center"
            >
                <Spinner />
            </View>
        );
    }

    if (isTransactionsError) {
        return (
            <YStack
                width="100%"
                minHeight={200}
                justifyContent="center"
                alignItems="center"
                gap={16}
            >
                <BodyMediumRegular color="$gray1000">
                    Could not load transactions
                </BodyMediumRegular>
                {onRetryTransactions != null && (
                    <PillButton
                        text="Try again"
                        onPress={onRetryTransactions}
                    />
                )}
            </YStack>
        );
    }

    const dayBoxes = selectedSpendData
        .sort((a, b) => b.date.getTime() - a.date.getTime())
        .map((day) => (
            <DayBox
                key={day.date.toString()}
                day={day}
                onOpenTransaction={onOpenTransaction}
            />
        ));

    const isEmbedded = dayListMaxHeight != null;

    const handleChartAreaLayout = (event: LayoutChangeEvent) => {
        const width = event.nativeEvent.layout.width;
        if (width <= 0) {
            return;
        }
        if (Platform.OS === 'web') {
            if (isWebChartWidthLockedRef.current) {
                return;
            }
            setChartWidth(Math.min(WEB_SPEND_CHART_WIDTH, width));
            return;
        }
        setChartWidth(width);
    };

    const chartSection = (
        <>
            {chartPeriodCaption != null || selectedBarTotalCents != null ? (
                <YStack paddingHorizontal={16}>
                    {chartPeriodCaption != null ? (
                        <BodySmallRegular color="$gray1000">
                            {chartPeriodCaption}
                        </BodySmallRegular>
                    ) : null}
                    {selectedBarTotalCents != null ? (
                        <H2Regular>
                            {centsToFormattedDollars(
                                selectedBarTotalCents,
                                selectedBarTotalCents > 0,
                            )}
                        </H2Regular>
                    ) : null}
                </YStack>
            ) : null}
            {chartWidth != null &&
            chartWidth > 0 &&
            (Platform.OS !== 'web' || shouldRenderWebChart) ? (
                <SpendPeriodBarChart
                    chartWidth={chartWidth}
                    chartData={chartData}
                    chartYAxisMax={chartYAxisMax}
                    showBudgetReferenceLine={showBudgetReferenceLine}
                    budget={budget}
                    focusedBarChartIndex={focusedBarChartIndex}
                    onSelectPeriod={setSelectedPeriodKey}
                />
            ) : (
                <View height={202} width="100%" />
            )}
        </>
    );

    if (!isEmbedded) {
        return (
            <>
                <View
                    width="100%"
                    backgroundColor="$gray200"
                    borderRadius={24}
                    justifyContent="flex-start"
                    alignItems="flex-start"
                    overflow="hidden"
                    paddingVertical={16}
                    onLayout={handleChartAreaLayout}
                    gap={24}
                >
                    {chartSection}
                </View>
                <YStack flex={1} marginTop={24} width="100%" gap={24}>
                    {dayBoxes}
                </YStack>
            </>
        );
    }

    // Chat embed: the sheet's gray sections sit on a white card, and the day
    // list scrolls inside it with a bottom fade while more days remain below.
    const hasMoreBelow =
        dayListContentHeight - dayListViewportHeight - dayListScrollY >
        SCROLL_EDGE_THRESHOLD;

    const dayListContent = (
        <YStack
            gap={EMBED_DAY_GAP}
            paddingHorizontal={EMBED_CARD_PADDING}
            paddingTop={EMBED_DAY_GAP}
            paddingBottom={EMBED_CARD_PADDING}
        >
            {dayBoxes}
        </YStack>
    );

    return (
        <YStack
            width="100%"
            backgroundColor="$white"
            borderRadius={24}
            overflow="hidden"
            paddingTop={EMBED_CARD_PADDING}
        >
            <View
                backgroundColor="$gray200"
                borderRadius={24}
                justifyContent="flex-start"
                alignItems="flex-start"
                overflow="hidden"
                marginHorizontal={EMBED_CARD_PADDING}
                paddingVertical={16}
                onLayout={handleChartAreaLayout}
                gap={24}
            >
                {chartSection}
            </View>
            <YStack width="100%" position="relative">
                {Platform.OS === 'web' ? (
                    <ScrollView
                        maxHeight={dayListMaxHeight}
                        scrollEventThrottle={16}
                        onLayout={handleDayListLayout}
                        onContentSizeChange={handleDayListContentSizeChange}
                        onScroll={handleDayListScroll}
                    >
                        {dayListContent}
                    </ScrollView>
                ) : (
                    <RNScrollView
                        style={{ maxHeight: dayListMaxHeight }}
                        nestedScrollEnabled
                        showsVerticalScrollIndicator={false}
                        scrollEventThrottle={16}
                        onLayout={handleDayListLayout}
                        onContentSizeChange={handleDayListContentSizeChange}
                        onScroll={handleDayListScroll}
                    >
                        {dayListContent}
                    </RNScrollView>
                )}
                {hasMoreBelow ? (
                    <LinearGradient
                        colors={[SCROLL_FADE_TRANSPARENT, EMBED_CARD_HEX]}
                        start={{ x: 0.5, y: 0 }}
                        end={{ x: 0.5, y: 1 }}
                        pointerEvents="none"
                        style={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            bottom: 0,
                            height: SCROLL_FADE_HEIGHT,
                        }}
                    />
                ) : null}
            </YStack>
        </YStack>
    );
};
